import React, { useState, useEffect } from "react";
import { DropdownMenu } from "../DropdownMenu";
import TextInput from "../TextInput";

interface CmrtV2ComplianceAnswerModel {
    Answer: string;
    Comment: string;
}

interface DropdownMenuAndCommentProps<T extends string> {
    localizer: any;
    value: string;
    options: T[];
    onValueChange: (value: string) => void;
    getFriendlyNameForOptions: (option: T) => string;
    getUniqueValueForOption: (option: T) => string;
    isDisabled?: boolean;
    label: string;
}

const DropdownMenuAndComment = <T extends string>({
    localizer,
    value,
    options,
    onValueChange,
    getFriendlyNameForOptions,
    getUniqueValueForOption,
    isDisabled,
    label,
}: DropdownMenuAndCommentProps<T>) => {
    const placeholderValue = options[0] === "Choose One" ? "" : options[0];
    const defaultAnswer = placeholderValue;
    const defaultComment = "";

    const parseValue = (input: string): CmrtV2ComplianceAnswerModel => {
        const parsed = JSON.parse(input && input.trim() !== "" ? input : "{}") as CmrtV2ComplianceAnswerModel;
        return {
            Answer: parsed?.Answer || defaultAnswer,
            Comment: parsed?.Comment || defaultComment,
        }
    };

    const [parsedValue, setParsedValue] = useState<CmrtV2ComplianceAnswerModel>(parseValue(value));

    useEffect(() => {
        setParsedValue(parseValue(value));
    }, [value]);

    const handleDropdownChange = (answer: T) => {
        // Convert "Choose One" to empty string
        const finalAnswer = answer === "Choose One" ? "" : answer;
        setParsedValue(prev => {
            const updated = { ...prev, Answer: finalAnswer };
            onValueChange(JSON.stringify(updated));
            return updated;
        });
    };

    const handleCommentChange = (comment: string) => {
        setParsedValue(prev => {
            const updated = { ...prev, Comment: comment };
            onValueChange(JSON.stringify(updated));
            return updated;
        });
    };

    return (
        <div className="row">
            <div className="form-group col-xs-3">
                <label className="dropdownLabel">{label}</label>
                <DropdownMenu
                    options={options}
                    value={parsedValue.Answer as T}
                    onOptionSelected={handleDropdownChange}
                    getFriendlyNameForOptions={getFriendlyNameForOptions}
                    getUniqueValueForOption={getUniqueValueForOption}
                    isDisabled={isDisabled}
                />
            </div>
            <div className="form-group col-xs-9">
                <label>{localizer.localizeString("Comment")}</label>
                <TextInput
                    value={parsedValue.Comment}
                    onValueChange={handleCommentChange}
                    disabled={isDisabled}
                />
            </div>
        </div>
    );
};

export default DropdownMenuAndComment;